/* =========================================================
   Affiche TENNIS DE TABLE « Le match » — le score set par set.
   API : SNTTMatch.render(data, opts) -> string SVG
     data : { mode:'sets', titre, kicker, adversaire, date, club, division,
              points, lieu, joueur,
              e1..e7 : un set par champ, au format « 11|8 » (moi|adversaire) }
     opts : { palette }

   Le nombre de sets gagnés de part et d'autre est CALCULÉ à partir des sets
   saisis : rien à additionner pour le client. Un set n'est comptabilisé que
   s'il porte deux nombres — un champ à moitié rempli est ignoré plutôt que
   compté à tort.

   Auto-suffisant (aucun fetch, aucune image) → re-render serveur direct.
   ========================================================= */
(function () {
  const PW = 800, PH = 1066, MX = 64;

  const PALETTES = {
    craie: { label:'Craie',      bg:'#F7F4ED', ink:'#0B2D5B', muted:'#8B94A3', accent:'#FF6B2C', line:'#E3DDD0', card:'#FFFFFF', table:'#1F4E8C', deep:'#12365F' },
    bleu:  { label:'Bleu salle', bg:'#0E2A4A', ink:'#F4F1E8', muted:'#8FA6BF', accent:'#FF6B2C', line:'#1C4470', card:'#15355C', table:'#1F4E8C', deep:'#0A1F38' },
    vert:  { label:'Vert table', bg:'#10362C', ink:'#F4F1E8', muted:'#8FB3A5', accent:'#FF6B2C', line:'#1C5544', card:'#164536', table:'#1B5E4A', deep:'#0B271F' },
    nuit:  { label:'Nuit',       bg:'#0B2D5B', ink:'#F7F4ED', muted:'#93A1B8', accent:'#FF9E5C', line:'#1E4272', card:'#12355F', table:'#123E70', deep:'#082444' },
  };

  const esc = s => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const up = s => String(s == null ? '' : s).toUpperCase();
  const num = s => { const m = String(s == null ? '' : s).replace(',', '.').match(/-?\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; };
  const fit = (t, maxW, maxS, cwf, ls, minS) => { const n = String(t).length || 1; minS = minS || 12;
    for (let s = maxS; s > minS; s -= 1) if (n * (cwf * s + ls) <= maxW) return s; return minS; };
  const T = (x,y,size,fill,txt,o) => { o = o || {};
    return `<text x="${x}" y="${y}" font-family="${o.f||'Sora'}" font-size="${size}" font-weight="${o.w||600}" fill="${fill}"`
      + (o.ls?` letter-spacing="${o.ls}"`:'') + ` text-anchor="${o.a||'middle'}"` + (o.op?` opacity="${o.op}"`:'') + `>${txt}</text>`; };

  function stats(p, items, y, x0) {
    x0 = x0 || 54;
    const it = items.filter(x => String(x[0] || '').trim());
    if (!it.length) return '';
    const w = (PW - 2*x0) / it.length;
    let s = `<line x1="${x0}" y1="${y-34}" x2="${PW-x0}" y2="${y-34}" stroke="${p.line}" stroke-width="1.5"/>`;
    it.forEach((c, i) => {
      const cc = x0 + w*i + w/2;
      if (i) s += `<line x1="${(x0+w*i).toFixed(0)}" y1="${y-14}" x2="${(x0+w*i).toFixed(0)}" y2="${y+34}" stroke="${p.line}" stroke-width="1.5"/>`;
      s += T(cc, y+6, fit(c[0], w-24, 38, 0.56, 0, 17), p.ink, esc(c[0]), { f:'Oswald', w:700 })
        + T(cc, y+30, 12, p.muted, esc(up(c[1])), { w:600, ls:2.5 });
    });
    return s;
  }

  /* Le troisième chiffre du bandeau n'a pas le même nom selon le sport : des
     points au ping, une série de classement au badminton, un rang au padel.
     Dérivé de `sport`, déjà présent dans _perso : aucun câblage supplémentaire. */
  const LIB_POINTS = { badminton: 'Classement', padel: 'Mon rang' };

  function render(data, opts) {
    data = data || {}; opts = opts || {};
    const p = PALETTES[opts.palette] || PALETTES.craie;
    const cx = PW / 2;

    const titre = up(data.titre) || 'LE MATCH';
    const ts = fit(titre, 690, 74, 0.54, 2, 32);
    const kicker = up(data.kicker) || up(data.division) || 'CHAMPIONNAT PAR ÉQUIPES';
    const sous = [data.adversaire ? 'CONTRE ' + up(data.adversaire) : '', up(data.date)].filter(Boolean).join(' · ');

    /* Un set ne compte que s'il porte DEUX nombres. */
    const sets = [];
    for (let i = 1; i <= 7; i++) {
      const raw = String(data['e' + i] || '').trim(); if (!raw) continue;
      const parts = raw.split(/[|\/\-–]/).map(s => (s || '').trim());
      const a = num(parts[0]), b = num(parts[1]);
      if (a == null || b == null) continue;
      sets.push([a, b]);
    }

    let corps = '', total = '';
    if (sets.length) {
      /* Les sets OCCUPENT toute la bande disponible quel qu'en soit le nombre :
         un match en 3 sets doit remplir la page comme un match en 7, sinon il
         reste un trou entre la dernière ligne et le pavé du total. */
      const BANDE_Y0 = 300, BANDE_Y1 = 762;
      const pas = (BANDE_Y1 - BANDE_Y0) / sets.length;
      const h = Math.min(78, pas - 16);
      const y0 = BANDE_Y0 + pas / 2;
      sets.forEach((s, i) => {
        const y = y0 + i * pas, gagne = s[0] > s[1];
        corps += `<rect x="${MX}" y="${(y - h/2).toFixed(1)}" width="${PW-2*MX}" height="${h}" rx="12" fill="${gagne ? p.card : 'none'}" stroke="${p.line}" stroke-width="1.5"/>`
          + T(MX + 34, y + 5, 15, p.muted, 'SET ' + (i + 1), { w:700, ls:2, a:'start' })
          + T(cx - 34, y + 9, 34, gagne ? p.accent : p.ink, String(s[0]), { f:'Oswald', w:700, a:'end' })
          + T(cx, y + 7, 20, p.muted, '–', { w:600 })
          + T(cx + 34, y + 9, 34, gagne ? p.ink : p.muted, String(s[1]), { f:'Oswald', w:700, a:'start' });
      });
      const g = sets.filter(s => s[0] > s[1]).length, pr = sets.length - g;
      total = `<rect x="${MX}" y="792" width="${PW-2*MX}" height="118" rx="14" fill="${p.ink}"/>`
        + T(cx, 832, 14, p.accent, 'SETS', { w:700, ls:8 })
        + `<text x="${cx}" y="888" text-anchor="middle" font-family="Oswald" font-weight="700" fill="${p.bg}"><tspan font-size="56">${g}</tspan><tspan font-size="26" fill="${p.muted}" dx="10">–</tspan><tspan font-size="56" dx="10">${pr}</tspan></text>`;
    } else {
      corps = T(cx, 600, 21, p.muted, 'SAISISSEZ AU MOINS UN SET, AU FORMAT 11 8', { w:600, ls:3 });
    }

    /* La ligne du nom reste à 30 px du bord : plus bas, elle paraît coupée
       une fois l'affiche encadrée. */
    const barre = stats(p, [[data.points, LIB_POINTS[data.sport] || 'Mes points'], [data.division, 'Division'], [data.club, 'Club']], 968);

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
${T(cx, 84, 19, p.accent, esc(kicker), { w:700, ls:5 })}
${T(cx, 168, ts, p.ink, esc(titre), { f:'Oswald', w:700, ls:2 })}
${sous ? T(cx, 204, fit(sous, 660, 17, 0.66, 3.5, 11), p.muted, esc(sous), { w:600, ls:3.5 }) : ''}
${corps}
${total}
${barre}
${data.joueur ? T(cx, 1030, 14, p.muted, esc(up(data.joueur)), { w:600, ls:4 }) : ''}
</svg>`;
  }

  window.SNTTMatch = { render, PALETTES };
})();
